import { useTranslation } from 'react-i18next';
import { AxiosError, AxiosResponse } from 'axios';
import { uniq } from 'lodash';
import { useBackdropContext } from '../../../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../../../context/installation';
import { ControlModeInternal } from '../../../../../../../data-access/gql-types/graphql';
import { useErrors } from '../../../../../../../hooks/errors/use-errors';
import { convertErrorsToOneString } from '../../../../../../../utils/helpers/errors';
import { scrollTop } from '../../../../../../../utils/helpers/scroll';
import { timeFormatted, timeToMilliseconds } from '../../../../../../../utils/helpers/time-convert';
import { toastError } from '../../../../../../../utils/toast';
import { useMeterSetEventingRules } from '../../../../../api/meter/meter.hooks';
import { ChannelType } from '../../../../../types/channel';
import {
  ControlModeInternalRequest,
  SourceThresholdInternalRequest,
} from '../../../../../types/graphql-rest-converter';
import { normalizeErrorCode } from '../../../../../utils/helpers/errors';
import { useMeterConfigurationContext } from '../context';
import { EventingRuleBody, MeterConfigurationBody, RuleError, ThresholdCondition } from '../types';

export const useMeterConfigurationSubmit = (channel: ChannelType, setTimeoutError: () => void) => {
  const { t } = useTranslation('backend-validation');
  const { t: ta } = useTranslation('action');
  const { selectedInstallationId } = useInstallationContext();
  const { form, setServerErrors, clearServerErrors } = useMeterConfigurationContext();
  const { handleRestErrors } = useErrors();
  const { mutate } = useMeterSetEventingRules();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();

  const onSubmit = form.handleSubmit(
    (values: MeterConfigurationBody) => {
      if (!selectedInstallationId) return;
      turnOnBackdrop();
      clearServerErrors();

      mutate(
        {
          installationId: selectedInstallationId,
          channelId: channel.id,
          eventingRules: values.rules.map((rule, index) => ({
            enabled: rule.enabled,
            controlMode: ControlModeInternalRequest[rule.controlMode],
            ...(rule.controlMode === ControlModeInternal.Threshold && {
              thresholdEvent: {
                source: SourceThresholdInternalRequest[rule.source],
                hysteresis: rule.hysteresis,
                deadTimeInMs: timeToMilliseconds(rule.deadTimeInMs),
                ...(rule.thresholdCondition === ThresholdCondition.LesserThan &&
                  rule.maxValue != null && {
                    maxValue: rule.maxValue,
                  }),
                ...(rule.thresholdCondition === ThresholdCondition.GreaterThan &&
                  rule.minValue != null && {
                    minValue: rule.minValue,
                  }),
              },
            }),
            ...(rule.controlMode === ControlModeInternal.DynamicPrice && {
              priceEvent: {
                minPrice: rule.minPriceChecked ? Number(rule.minPrice) : null,
                maxPrice: rule.maxPriceChecked ? Number(rule.maxPrice) : null,
                startAt: rule.startAt,
                endAt: rule.endAt,
                cheapestPeriods: rule.cheapestPeriodsChecked ? Number(rule.cheapestPeriods) : null,
              },
            }),
            action: {
              index: index + 1,
              isDefaultAction: rule.isDefaultAction,
              isNotifyAction: rule.isNotifyAction,
            },
          })),
        },
        {
          onSuccess: () => {
            setTimeoutError();
          },
          onError: (error: AxiosError) => {
            const globalErrors: string[] = [];

            scrollTop();
            turnOffBackdrop();
            const response = error.response;

            if (response) {
              if ('errors' in (response as AxiosResponse).data) {
                const errorList = (response as AxiosResponse).data.errors as string[];

                for (const x of errorList) {
                  const errorParsed = JSON.parse(x) as RuleError;
                  const errorCode = errorParsed.ErrorCode;
                  const values = errorParsed.FormattedMessagePlaceholderValues;
                  const index = values.CollectionIndex;
                  const property = values.PropertyPath.split('.').pop();

                  if (property) {
                    if (property === 'EventingRules') {
                      globalErrors.push(t(normalizeErrorCode(errorCode)));
                    } else {
                      const camelProp = (property.charAt(0).toLowerCase() +
                        property.slice(1)) as keyof EventingRuleBody;

                      setServerErrors((prev) => {
                        return {
                          ...prev,
                          [index]: {
                            ...prev[index],
                            [camelProp]: t(normalizeErrorCode(errorCode), {
                              from: values.From,
                              to: values.To,
                              ...(values.ComparisonValue != null && {
                                value:
                                  camelProp !== 'deadTimeInMs'
                                    ? values.ComparisonValue
                                    : timeFormatted(values.ComparisonValue, ta),
                              }),
                            }),
                          },
                        };
                      });
                    }
                  }
                }
              }
            }

            if (globalErrors.length) toastError({ content: convertErrorsToOneString(uniq(globalErrors)) });
            else {
              handleRestErrors(error);
            }
          },
        },
      );
    },
    () => scrollTop(),
  );

  return {
    onSubmit,
  };
};
